


























    import {
        defineComponent,
        computed,
        ref,
        watch,
        nextTick,
        onMounted,
        onUnmounted
    } from "@vue/composition-api";
    import { Chart, ChartItem, registerables, TimeUnit } from "chart.js";
    import "chartjs-adapter-date-fns";
    import useCurrentStatistic, { CurrentDataPoint } from "@/store/useCurrentStatistic";

    Chart.register(...registerables);

    export default defineComponent({
        props: {
            imgSrc: { type: String },
            value: { type: Boolean, default: false },
            deviceType: { type: Number, default: 0 },
            id: { type: String, required: true },
            subtitle: { type: String, default: "" }
        },
        setup(props, context) {
            const chartCanvas = ref<HTMLCanvasElement>();
            const toggle_chart = ref<number>(0);
            const dialog = computed(() => props.value);

            const { getCurrentData } = useCurrentStatistic();
            let chart: Chart<"line", { x: Date; y: number }[], unknown> | null = null;

            const getChartConfig = (value: number): { scale: TimeUnit; title: string } => {
                switch (value) {
                    case 0: return { scale: "day", title: "Daily current (7 Days)" };
                    case 1: return { scale: "day", title: "Daily current (30 Days)" };
                    case 2: return { scale: "week", title: "Weekly current (90 Days)" };
                    case 3: return { scale: "month", title: "Monthly current (365 Days)" };
                    default: return { scale: "day", title: "Current" };
                }
            };

            const setupChart = async () => {
                const ctx = chartCanvas.value as ChartItem;
                if (!ctx) return;

                const interval = [7, 30, 90, 365][toggle_chart.value] || 7;
                const { scale, title } = getChartConfig(toggle_chart.value);

                const data = await getCurrentData(props.id, interval);

                // Separate datasets
                const amperageData = data.map(d => ({
                    x: d.date,
                    y: d.amperage
                }));

                const isRunningData = data.map(d => ({
                    x: d.date,
                    y: d.isRunning ? 1 : 0
                }));

                chart = new Chart<"line", { x: Date; y: number }[], unknown>(ctx, {
    type: "line",
    data: {
        datasets: [
            {
                label: "Amperage",
                data: amperageData,
                parsing: false,
                yAxisID: "y",
                borderColor: "rgb(75, 192, 192)",
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                fill: true,
                tension: 0.1,
                pointRadius: 9,
                pointHitRadius: 3,
                pointHoverRadius: 12
            },
            {
                label: "Running",
                data: isRunningData,
                parsing: false,
                yAxisID: "y1",
                borderColor: "#ff9800",
                borderDash: [5, 5],
                stepped: true,
                fill: false,
                tension: 0,
                pointRadius: 0
            }
        ]
    },
    options: {
        plugins: {
            title: {
                display: true,
                text: title,
                align: "center",
                position: "top",
                padding: { top: 10, bottom: 10 }
            },
            subtitle: {
                display: true,
                text: props.subtitle,
                align: "center",
                padding: { bottom: 10 }
            },
            legend: {
                display: true
            }
        },
        parsing: false, // optional, handled at dataset level already
        scales: {
            x: {
                type: "time",
                time: {
                    unit: scale,
                    displayFormats: {
                        day: "dd-MMM",
                        week: "dd-MMM",
                        month: "MMM yyyy",
                        quarter: "MMM yyyy",
                        year: "yyyy"
                    }
                }
            },
            y: {
                title: {
                    display: true,
                    text: "Amperage"
                },
                beginAtZero: true
            },
            y1: {
                position: "right",
                grid: {
                    drawOnChartArea: false
                },
                title: {
                    display: true,
                    text: "Running"
                },
                min: -0.1,
                max: 1.1,
                ticks: {
                    stepSize: 1,
                    callback: (val) => (val === 1 ? "On" : "Off")
                }
            }
        }
    }
});

                (chart.canvas as HTMLCanvasElement).style.width = "auto";
            };

            const rebuildChart = async () => {
                if (chart) {
                    chart.destroy();
                    chart = null;
                }
                await nextTick();
                setupChart();
            };

            const close = () => {
                if (chart) {
                    chart.destroy();
                    chart = null;
                }
                context.emit("input", false);
            };

            onMounted(() => {
                if (props.value) {
                    setupChart();
                }
            });

            onUnmounted(() => {
                if (chart) {
                    chart.destroy();
                    chart = null;
                }
            });

            watch(() => props.value, (val) => {
                if (val) rebuildChart();
            });

            watch(() => toggle_chart.value, () => {
                rebuildChart();
            });

            return {
                dialog,
                close,
                chartCanvas,
                toggle_chart
            };
        }
    });
